/**
 * 抽查任务列表查询与分组计数
 *
 * 数据来自 api-server GraphQL，后端经 Dubbo 转发至 swiftx-quality-service。
 */
import {useCallback, useMemo, useRef, useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {message} from 'antd';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {
    SpotTaskConclusion,
    SpotTaskFilters,
    SpotTaskRow,
    SpotTaskStats,
    SpotTaskStatus,
    SpotTaskTab,
} from '../types/ExemptSpotCheckType';
import {
    GET_SPOT_TASKS,
    GET_SPOT_TASK_GROUP_COUNTS,
    GET_SPOT_TASK_STATS,
    GetSpotTaskGroupCountsResponse,
    GetSpotTaskStatsResponse,
    GetSpotTasksResponse,
    SpotTaskFiltersInput,
} from '../graphql/queries/spotTaskQueries';
import {toSpotTaskRow} from '../graphql/spotTaskConverter';
import {isOfflineConclusion} from '../config/exemptSpotCheckConfig';

interface UseSpotTaskQueryResult {
    rows: SpotTaskRow[];
    stats?: SpotTaskStats;
    total: number;
    loading: boolean;
    tabCounts: Record<SpotTaskTab, number>;
    executeQuery: (filters: SpotTaskFilters, page: number, pageSize: number, tab: SpotTaskTab) => void;
    /**
     * 先置加载态，用于本次查询要等前置请求（如期号）才能发起的场景。
     * 不置位的话这段时间表格显示「暂无数据」，比转圈更容易被误读成真没数据。
     */
    beginLoading: () => void;
}

/**
 * 任务归入哪个分组。
 *
 * 待抽查：尚未完成（status = PENDING）
 * 已通过：结论为 MAINTAIN
 * 不通过：结论为 OFFLINE 或 OFFLINE_BLOCKED
 */
export const resolveTaskTab = (row: SpotTaskRow): SpotTaskTab => {
    if (row.status !== SpotTaskStatus.DONE) {
        return SpotTaskTab.PENDING;
    }
    // 两档下线都归「不通过」组：漏判会让禁报档的任务落进「已通过」，与结论标签自相矛盾
    if (isOfflineConclusion(row.conclusion)) {
        return SpotTaskTab.FAILED;
    }
    return SpotTaskTab.PASSED;
};

/**
 * 当前 Tab → 后端筛选入参。
 *
 * Tab 的分组维度不统一：待抽查看 status，其余看 conclusion，
 * 故在此按 Tab 拆成不同的筛选条件下发。
 */
const buildFiltersInput = (filters: SpotTaskFilters, tab?: SpotTaskTab): SpotTaskFiltersInput => {
    const input: SpotTaskFiltersInput = {
        periodNo: filters.periodNo,
        source: filters.source,
        targetType: filters.targetType,
        assigneeId: filters.assigneeId,
        dspId: filters.dspId,
    };
    switch (tab) {
        case SpotTaskTab.PENDING:
            input.statuses = [SpotTaskStatus.PENDING];
            break;
        case SpotTaskTab.PASSED:
            input.conclusions = [SpotTaskConclusion.MAINTAIN];
            break;
        case SpotTaskTab.FAILED:
            input.conclusions = [SpotTaskConclusion.OFFLINE, SpotTaskConclusion.OFFLINE_BLOCKED];
            break;
        default:
            break;
    }
    return input;
};

const EMPTY_TAB_COUNTS: Record<SpotTaskTab, number> = {
    [SpotTaskTab.PENDING]: 0,
    [SpotTaskTab.PASSED]: 0,
    [SpotTaskTab.FAILED]: 0,
};

export const useSpotTaskQuery = (): UseSpotTaskQueryResult => {
    const client = useApolloClient();
    const [rows, setRows] = useState<SpotTaskRow[]>([]);
    const [stats, setStats] = useState<SpotTaskStats | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [rawTabCounts, setRawTabCounts] = useState<Record<string, number>>({});
    /**
     * 请求序号：切分组/翻页/换周期时前一次请求可能仍在途，
     * 后到的旧响应会覆盖新结果（列表与高亮的 tab 对不上）。只认最后一次发起的。
     */
    const requestSeq = useRef(0);

    const executeQuery = useCallback(
        (filters: SpotTaskFilters, page: number, pageSize: number, tab: SpotTaskTab) => {
            const seq = ++requestSeq.current;
            setLoading(true);
            const listPromise = client.query<GetSpotTasksResponse>({
                query: GET_SPOT_TASKS,
                variables: {filters: buildFiltersInput(filters, tab), pageNum: page, pageSize},
                fetchPolicy: 'network-only',
            });
            // 分组计数不带 tab（要出全部分组），统计卡按周期取
            const countsPromise = client.query<GetSpotTaskGroupCountsResponse>({
                query: GET_SPOT_TASK_GROUP_COUNTS,
                variables: {filters: buildFiltersInput(filters)},
                fetchPolicy: 'network-only',
            });
            const statsPromise = client.query<GetSpotTaskStatsResponse>({
                query: GET_SPOT_TASK_STATS,
                variables: {periodNo: filters.periodNo ?? null},
                fetchPolicy: 'network-only',
            });

            Promise.all([listPromise, countsPromise, statsPromise])
                .then(([listRes, countsRes, statsRes]) => {
                    if (seq !== requestSeq.current) {
                        return;
                    }
                    const page = listRes.data?.podExemptSpotTasks;
                    setRows((page?.content ?? []).map(toSpotTaskRow));
                    setTotal(page?.total ?? 0);

                    const counts: Record<string, number> = {};
                    (countsRes.data?.podExemptSpotTaskGroupCounts ?? []).forEach((item) => {
                        counts[item.groupKey] = item.count;
                    });
                    setRawTabCounts(counts);

                    setStats(statsRes.data?.podExemptSpotTaskStats);
                })
                .catch((e) => {
                    if (seq !== requestSeq.current) {
                        return;
                    }
                    setRows([]);
                    setTotal(0);
                    setRawTabCounts({});
                    setStats(undefined);
                    message.error(translateGraphQLError(e, 'exemptSpotCheck.messages.loadTaskFailed'));
                })
                .finally(() => {
                    // 过期请求不关 loading：新请求仍在途，关掉会让页面短暂显示「加载完毕的空列表」
                    if (seq === requestSeq.current) {
                        setLoading(false);
                    }
                });
        },
        [client],
    );

    const tabCounts = useMemo(
        () => ({
            [SpotTaskTab.PENDING]: rawTabCounts[SpotTaskTab.PENDING] ?? 0,
            [SpotTaskTab.PASSED]: rawTabCounts[SpotTaskTab.PASSED] ?? 0,
            [SpotTaskTab.FAILED]: rawTabCounts[SpotTaskTab.FAILED] ?? 0,
        }),
        [rawTabCounts],
    );

    const beginLoading = useCallback(() => setLoading(true), []);

    return {
        rows,
        stats,
        total,
        loading,
        tabCounts: tabCounts ?? EMPTY_TAB_COUNTS,
        executeQuery,
        beginLoading,
    };
};
